import DownloadList from "@bmi-digital/components/download-list";
import Section from "@bmi-digital/components/section";
import { replaceSpaces } from "@bmi-digital/components/utils";
import { graphql } from "gatsby";
import React, { useRef, useState } from "react";
import { useConfig } from "../contexts/ConfigProvider";
import DocumentResultsFooter from "./DocumentResultsFooter";
import DocumentSimpleTableResults from "./DocumentSimpleTableResults";
import RichText from "./RichText";
import type {
  DocumentTableHeader,
  PayloadDocumentData
} from "../types/Document";
import type {
  PayloadDocumentDownloadSection,
  PayloadRichText
} from "../types/payload";

const DOCUMENTS_PER_PAGE = 24;
const TABLE_HEADERS: DocumentTableHeader[] = [
  "add",
  "title",
  "size",
  "actions"
];

export type DocumentDownloadSectionData = Pick<
  PayloadDocumentDownloadSection,
  "blockType" | "title"
> & {
  richTextDescription: PayloadRichText | null;
  documents: readonly PayloadDocumentData[] | null;
};
const DocumentDownloadSection = ({
  data: { title, richTextDescription, documents: allDocuments }
}: {
  data: DocumentDownloadSectionData;
}) => {
  const [page, setPage] = useState(1);
  const [documents, setDocuments] = useState(
    allDocuments.slice(0, DOCUMENTS_PER_PAGE)
  );
  const { documentDownloadMaxLimit } = useConfig();
  const documentsTable = useRef<HTMLDivElement>(null);
  const count = Math.ceil(allDocuments.length / DOCUMENTS_PER_PAGE);
  const maxSize = (documentDownloadMaxLimit || 0) * 1000000;

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    const scrollY = documentsTable.current
      ? documentsTable.current.offsetTop - 200
      : 0;
    window.scrollTo(0, scrollY);
    setPage(page);
    setDocuments(
      allDocuments.slice(
        (page - 1) * DOCUMENTS_PER_PAGE,
        page * DOCUMENTS_PER_PAGE
      )
    );
  };

  return (
    <Section
      backgroundColor="white"
      data-testid={`document-download-section-${replaceSpaces(title)}`}
    >
      {title && <Section.Title>{title}</Section.Title>}
      {richTextDescription && (
        <div style={{ marginBottom: "40px" }}>
          <RichText document={richTextDescription} />
        </div>
      )}
      {documents.length > 0 && (
        <div
          ref={documentsTable}
          data-testid={"document-download-section-table"}
        >
          <DownloadList maxSize={maxSize}>
            <DocumentSimpleTableResults
              documents={documents}
              headers={TABLE_HEADERS}
              pageNumber={page}
            />
            <DocumentResultsFooter
              page={page}
              count={count}
              onPageChange={handlePageChange}
            />
          </DownloadList>
        </div>
      )}
    </Section>
  );
};

export default DocumentDownloadSection;

export const query = graphql`
  fragment DocumentDownloadSectionFragment on PayloadDocumentDownloadSection {
    blockType
    title
    richTextDescription: description {
      ...RichTextFragment
    }
    documents {
      ...DocumentFragment
    }
  }
`;
